import React, { useState, useEffect, useRef } from "react";
import { UploadBox, PrimaryButton } from "./components";
import { useToast } from "../../../../hooks/useToast";
import providersService from "../../../../api/services/providersService";
import { validateUploadFile } from "../../../../utils/directUpload";

const isDocumentValue = (value) => {
  if (!value) return false;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (/^[a-f0-9]{24}$/i.test(trimmed)) return false;
    return /^https?:\/\//i.test(trimmed) || trimmed.includes("/") || /\.[a-z0-9]{2,4}$/i.test(trimmed);
  }
  if (typeof value === "object") {
    return Boolean(
      value.url ||
        value.file?.url ||
        value.fileUrl ||
        value.path ||
        value.filename
    );
  }
  return false;
};

const pickDocumentSource = (candidates) => {
  for (const candidate of candidates) {
    if (isDocumentValue(candidate)) return candidate;
  }
  return "";
};

const getFilenameFromUrl = (value = "") => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.split("?")[0];
  const parts = trimmed.split("/");
  return parts[parts.length - 1] || "";
};

const resolveExistingName = (source) => {
  if (!source) return "";
  if (typeof source === "string") return getFilenameFromUrl(source) || source;
  if (source.file) return resolveExistingName(source.file);
  return source.name || getFilenameFromUrl(source.url || source.file?.url || "");
};

export default function LicensePanel({ profile, onRequestReview }) {
  const toast = useToast();
  const licenseInputRef = useRef(null);
  const driverInputRef = useRef(null);

  const [documentFiles, setDocumentFiles] = useState({
    license: null,
    driver: null,
  });
  const [documentNames, setDocumentNames] = useState({
    license: "",
    driver: "",
  });
  const [existingDocumentNames, setExistingDocumentNames] = useState({
    license: "",
    driver: "",
  });
  const [isSavingLicense, setIsSavingLicense] = useState(false);

  useEffect(() => {
    if (profile) {
      const documentsRoot = profile.profile ?? profile;
      const licenseSource = pickDocumentSource([
        documentsRoot.license,
        documentsRoot.licenseDocument,
        documentsRoot.license_file,
        documentsRoot.licenseUrl,
        documentsRoot.documents?.license,
        documentsRoot.documents?.licenseDocument,
        documentsRoot.documents?.licenseUrl,
        documentsRoot.profile?.license,
        documentsRoot.profile?.documents?.license,
      ]);
      const driverSource = pickDocumentSource([
        documentsRoot.driversLicense,
        documentsRoot.driverLicense,
        documentsRoot.validId,
        documentsRoot.identification,
        documentsRoot.idDocument,
        documentsRoot.idUrl,
        documentsRoot.documents?.driversLicense,
        documentsRoot.documents?.driverLicense,
        documentsRoot.documents?.validId,
        documentsRoot.documents?.identification,
        documentsRoot.documents?.idDocument,
        documentsRoot.documents?.idUrl,
        documentsRoot.profile?.documents?.driverLicense,
        documentsRoot.profile?.documents?.validId,
      ]);

      setExistingDocumentNames({
        license: resolveExistingName(licenseSource),
        driver: resolveExistingName(driverSource),
      });
    }
  }, [profile]);

  const handleDocumentChange = (key, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Images only — the API has always rejected PDFs, so accepting them here
    // only produced a failure after the upload.
    const validationError = validateUploadFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setDocumentFiles((prev) => ({ ...prev, [key]: file }));
    setDocumentNames((prev) => ({ ...prev, [key]: file.name || "" }));
  };

  const validateLicenseForm = () => {
    const hasLicense = Boolean(documentFiles.license) || Boolean(existingDocumentNames.license);
    const hasDriver = Boolean(documentFiles.driver) || Boolean(existingDocumentNames.driver);

    if (!hasLicense) {
      toast.error("Please upload your tour guide licence.");
      return false;
    }
    if (!hasDriver) {
      toast.error("Please upload your driver’s licence.");
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!validateLicenseForm()) return;

    onRequestReview("license", async () => {
      setIsSavingLicense(true);
      try {
        const uploads = [];
        if (documentFiles.license) {
          uploads.push(providersService.updateLicense(documentFiles.license));
        }
        if (documentFiles.driver) {
          uploads.push(providersService.uploadIdDocument(documentFiles.driver));
        }
        await Promise.all(uploads);

        setExistingDocumentNames((prev) => ({
          license: documentNames.license || prev.license,
          driver: documentNames.driver || prev.driver,
        }));
        setDocumentFiles({ license: null, driver: null });
        setDocumentNames({ license: "", driver: "" });
        toast.success("License information updated successfully.");
        return true;
      } catch (error) {
        toast.apiError(error, "Unable to update license details.");
        return false;
      } finally {
        setIsSavingLicense(false);
      }
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <UploadBox
          title="Upload Tour Guide Licence"
          helper="To ensure optimal visuals, please upload a clear copy."
          fileName={documentNames.license || existingDocumentNames.license}
          buttonLabel="Choose File"
          onClick={() => licenseInputRef.current?.click()}
        >
          <input
            ref={licenseInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => handleDocumentChange("license", event)}
          />
        </UploadBox>

        <UploadBox
          title="Upload Drivers License"
          helper="Passport, driver’s licence or other accepted ID."
          fileName={documentNames.driver || existingDocumentNames.driver}
          buttonLabel="Choose File"
          onClick={() => driverInputRef.current?.click()}
        >
          <input
            ref={driverInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => handleDocumentChange("driver", event)}
          />
        </UploadBox>
      </div>

      <div className="flex justify-end pt-4">
        <PrimaryButton
          onClick={handleSaveClick}
          disabled={isSavingLicense}
          label={isSavingLicense ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
}
